import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SiteHeader from './SiteHeader';
import LoginModal from './LoginModal';

// Shown in place of a login-gated page when the user is signed out. It keeps the
// requested URL, so once the user signs in (auth state flips) the parent
// ProtectedRoute re-renders and the requested page loads — following through to
// where they were headed without a redirect.
const SignInRequired = () => {
  const [showLogin, setShowLogin] = useState(true);
  const [mode, setMode] = useState('login');
  const open = (m) => { setMode(m); setShowLogin(true); };
  return (
    <div className="min-h-screen bg-gray-50">
      <SiteHeader />
      <div className="max-w-md mx-auto px-4 py-24 text-center">
        <div className="w-14 h-14 rounded-2xl bg-videbits-50 flex items-center justify-center mx-auto mb-5">
          <Lock className="w-7 h-7 text-videbits-500" />
        </div>
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">Sign in to continue</h1>
        <p className="text-gray-500 mb-6">This page needs an account. Sign in and you'll land right back here.</p>
        <div className="flex items-center justify-center gap-3">
          <button onClick={() => open('login')} className="px-5 py-2.5 bg-videbits-600 text-white rounded-lg hover:bg-videbits-700 transition-colors font-medium">
            Sign In
          </button>
          <button onClick={() => open('signup')} className="px-5 py-2.5 border border-videbits-300 text-videbits-700 rounded-lg hover:bg-videbits-50 transition-colors font-medium">
            Sign Up
          </button>
        </div>
      </div>
      <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} initialMode={mode} />
    </div>
  );
};

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, isAuthenticated, isAdmin, loading } = useAuth();

  // Show loading spinner while auth is being checked
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-videbits-600 mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Signed out: prompt sign in on the same URL (don't redirect), so the user
  // follows through to this page once authenticated.
  if (!isAuthenticated || !user) {
    return <SignInRequired />;
  }

  // Signed in but not an admin where one is required — send home.
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default ProtectedRoute;
